import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { dashboardAPI, projectsAPI } from '../services/api';
import { 
  Building2, 
  TrendingUp, 
  FileText, 
  Clock, 
  ArrowRight,
  AlertCircle,
  CheckCircle2,
  DollarSign,
  BarChart3,
  Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const Dashboard = () => {
  const { user, isDeveloper } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashboardRes, projectsRes] = await Promise.all([
        isDeveloper() ? dashboardAPI.getDeveloper() : dashboardAPI.getConstructor(),
        projectsAPI.getAll()
      ]);
      
      // Verificar que las respuestas tengan la estructura esperada
      if (dashboardRes?.data?.data) {
        setDashboardData(dashboardRes.data.data);
      } else if (dashboardRes?.data) {
        setDashboardData(dashboardRes.data);
      } else {
        console.error('Respuesta de dashboard inválida:', dashboardRes);
        setDashboardData({});
      }
      
      if (projectsRes?.data?.data) {
        setProjects(Array.isArray(projectsRes.data.data) ? projectsRes.data.data.slice(0, 5) : []);
      } else if (projectsRes?.data) {
        setProjects(Array.isArray(projectsRes.data) ? projectsRes.data.slice(0, 5) : []);
      } else {
        console.error('Respuesta de projects inválida:', projectsRes);
        setProjects([]);
      }
    } catch (error) {
      console.error('Error al cargar datos del dashboard:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error('Error al cargar datos del dashboard: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const DeveloperDashboard = () => (
    <>
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5C554B] font-medium">Obras Totales</p>
                <p className="text-3xl font-bold text-[#2D2A26]">{dashboardData?.total_projects || 0}</p>
              </div>
              <div className="w-12 h-12 bg-[#C05621]/10 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[#C05621]" />
              </div>
            </div>
            <p className="text-xs text-[#81A183] mt-2">
              {dashboardData?.active_projects || 0} activas
            </p>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5C554B] font-medium">Presupuesto Total</p>
                <p className="text-2xl font-bold text-[#2D2A26]">{formatCurrency(dashboardData?.total_budget)}</p>
              </div>
              <div className="w-12 h-12 bg-[#81A183]/10 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-[#81A183]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5C554B] font-medium">Cotizaciones</p>
                <p className="text-3xl font-bold text-[#2D2A26]">{dashboardData?.pending_quotes || 0}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-amber-600" />
              </div>
            </div>
            <p className="text-xs text-amber-600 mt-2">Pendientes de revisión</p>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5C554B] font-medium">Por Autorizar</p>
                <p className="text-3xl font-bold text-[#2D2A26]">{dashboardData?.pending_approvals || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-blue-600 mt-2">Avances pendientes</p>
          </CardContent>
        </Card>
      </div>

      {/* Budget Progress */}
      <Card className="card-dashboard mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#2D2A26]">
            <BarChart3 className="w-5 h-5 text-[#C05621]" />
            Presupuesto vs Ejecutado
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-between text-sm">
              <span className="text-[#5C554B]">Ejecutado</span>
              <span className="font-semibold text-[#2D2A26]">
                {formatCurrency(dashboardData?.executed_budget)} de {formatCurrency(dashboardData?.total_budget)}
              </span>
            </div>
            <Progress 
              value={dashboardData?.total_budget > 0 
                ? (dashboardData?.executed_budget / dashboardData?.total_budget) * 100 
                : 0
              } 
              className="h-3"
            />
            <div className="flex justify-between text-xs text-[#5C554B]">
              <span>0%</span>
              <span>
                {dashboardData?.total_budget > 0 
                  ? Math.round((dashboardData?.executed_budget / dashboardData?.total_budget) * 100)
                  : 0}% completado
              </span>
              <span>100%</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );

  const ConstructorDashboard = () => (
    <>
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5C554B] font-medium">Obras Asignadas</p>
                <p className="text-3xl font-bold text-[#2D2A26]">{dashboardData?.assigned_projects || 0}</p>
              </div>
              <div className="w-12 h-12 bg-[#C05621]/10 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[#C05621]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5C554B] font-medium">Avances Pendientes</p>
                <p className="text-3xl font-bold text-[#2D2A26]">{dashboardData?.pending_advances || 0}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
            <p className="text-xs text-amber-600 mt-2">Por autorizar</p>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5C554B] font-medium">Avances Aprobados</p>
                <p className="text-3xl font-bold text-[#2D2A26]">{dashboardData?.approved_advances || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5C554B] font-medium">Total Cotizado</p>
                <p className="text-2xl font-bold text-[#2D2A26]">{formatCurrency(dashboardData?.total_quoted)}</p>
              </div>
              <div className="w-12 h-12 bg-[#81A183]/10 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-[#81A183]" />
              </div>
            </div>
            <p className="text-xs text-[#81A183] mt-2">
              {dashboardData?.accepted_quotes || 0} aceptadas
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-48 rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#2D2A26]">
              Hola, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-[#5C554B]">
              {isDeveloper() 
                ? 'Resumen de tus obras y actividades'
                : 'Resumen de tus asignaciones'
              }
            </p>
          </div>
          {isDeveloper() && (
            <Link to="/projects/new">
              <Button className="btn-primary" data-testid="new-project-btn">
                <Plus className="w-5 h-5 mr-2" />
                Nueva Obra
              </Button>
            </Link>
          )}
        </div>

        {/* Role-specific content */}
        {isDeveloper() ? <DeveloperDashboard /> : <ConstructorDashboard />}

        {/* Projects List */}
        <Card className="card-dashboard">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-[#2D2A26]">
              <Building2 className="w-5 h-5 text-[#C05621]" />
              {isDeveloper() ? 'Mis Obras' : 'Obras Asignadas'}
            </CardTitle>
            <Link to="/projects">
              <Button variant="ghost" size="sm" className="text-[#C05621]">
                Ver todas
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {projects.length === 0 ? (
              <div className="text-center py-8">
                <Building2 className="w-12 h-12 text-[#E2E8F0] mx-auto mb-3" />
                <p className="text-[#5C554B]">
                  {isDeveloper() 
                    ? 'No tienes obras creadas aún'
                    : 'No tienes obras asignadas'
                  }
                </p>
                {isDeveloper() && (
                  <Link to="/projects/new">
                    <Button variant="outline" className="mt-4">
                      Crear primera obra
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {projects.map((project) => (
                  <Link
                    key={project.id}
                    to={`/projects/${project.id}`}
                    className="flex items-center justify-between p-4 rounded-xl border border-[#E2E8F0] hover:border-[#C05621]/30 hover:bg-[#FDFBF7] transition-all"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-[#C05621]/10 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-[#C05621]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#2D2A26]">{project.name}</p>
                        <p className="text-sm text-[#5C554B]">{project.location || 'Sin ubicación'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`
                        px-3 py-1 rounded-full text-xs font-medium
                        ${project.status === 'activo' ? 'badge-active' : 
                          project.status === 'completado' ? 'badge-approved' : 'badge-pending'}
                      `}>
                        {project.status}
                      </span>
                      <ArrowRight className="w-4 h-4 text-[#9CA3AF]" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activities */}
        {dashboardData?.recent_activities?.length > 0 && (
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#2D2A26]">
                <TrendingUp className="w-5 h-5 text-[#C05621]" />
                Actividad Reciente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {dashboardData.recent_activities.map((activity, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                      ${activity.status === 'aprobado' ? 'bg-green-100' : 
                        activity.status === 'pendiente' ? 'bg-amber-100' : 'bg-red-100'}
                    `}>
                      {activity.status === 'aprobado' ? (
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      ) : activity.status === 'pendiente' ? (
                        <Clock className="w-4 h-4 text-amber-600" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-[#2D2A26]">
                        <span className="font-medium">{activity.project_name}</span>
                        {' - '}Avance {activity.status}
                      </p>
                      <p className="text-xs text-[#9CA3AF]">
                        {new Date(activity.created_at).toLocaleDateString('es-MX', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
